import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { MailDomainDmarc } from '../types/models/MailDomainDmarc';
import type { ApiResponseMailDomainDmarc } from '../types/api/ApiResponseMailDomainDmarc';

interface UseMailDomainDmarcReturn {
  data: MailDomainDmarc | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Reads the DMARC policy published on the mail domain's managed DNS zone (the `_dmarc` label).
 *
 * The backend performs a best-effort live PowerDNS read, so this is an expensive call — only invoke it
 * from a tab mounted lazily via `mountOnEnter`, never at page level. Per the API contract the response is
 * JSend wrapped and controller exceptions map to `status: 'error'` at HTTP 200, so we branch on
 * `body.status === 'success'` (+ the `dmarc` key), not `response.ok`. Note that a DNS read failure is NOT a
 * request failure: it returns `status: 'success'` with `dmarc_state: 'unknown'` and a safe `dmarc_error` —
 * the tab surfaces that, this hook only sets `error` for an actual transport/contract failure. Previous data
 * is kept during a refetch so the view stays visible while the Refresh button shows its loading state.
 */
export const useMailDomainDmarc = (mailDomainId: number | undefined): UseMailDomainDmarcReturn => {
  const [data, setData] = useState<MailDomainDmarc | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchDmarc = useCallback(async () => {
    if (!mailDomainId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/emails/domains/${mailDomainId}/dmarc`);
      const body = (await response.json()) as ApiResponseMailDomainDmarc;

      if (body.status === 'success' && body.data?.dmarc) {
        setData(body.data.dmarc);
      } else {
        setError(body.message ?? 'Failed to load DMARC record');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading the DMARC record');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, mailDomainId]);

  useEffect(() => {
    if (auth.isAuthenticated && mailDomainId) {
      fetchDmarc();
    }
  }, [auth.isAuthenticated, mailDomainId, fetchDmarc]);

  return { data, loading, error, refetch: fetchDmarc };
};
